// src/components/reviewerdash/ReviewForm.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Download,
  Eye,
  AlertCircle,
  CheckCircle,
  Save,
  Send,
  Loader,
  X,
  Calendar,
  Mail,
  Paperclip,
  Clock,
  Upload as UploadIcon,
  Trash2,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from '../../utils/axios';
import RichTextViewer from '../common/RichTextViewer';
import { showToast } from '../../utils/toast';
import { handleDownloadFile } from '../../utils/utils.global';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const ACCEPTED_EXTENSIONS = /\.(pdf|doc|docx|txt)$/i;
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const INACTIVITY_AUTOSAVE_MS = 2 * 60 * 1000; // 2 minutes

const fileNameFromUrl = (url) => {
  if (!url) return '';
  try {
    return decodeURIComponent(String(url).split('/').pop()) || String(url);
  } catch {
    return String(url);
  }
};

// ---------------------------------------------------------------------------
// Inline file upload control
// ---------------------------------------------------------------------------
const ReviewFileUpload = ({ name, value, uploading, onUpload, onClear }) => {
  const inputRef = useRef(null);
  const isUploaded = typeof value === 'string' && value.trim().length > 0;
  const displayName = fileNameFromUrl(value);

  const handlePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_EXTENSIONS.test(file.name)) {
      showToast('Please upload a PDF, Word document, or text file.', 'error');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      showToast('File size exceeds 20MB.', 'error');
      e.target.value = '';
      return;
    }

    onUpload(name, file);
    e.target.value = '';
  };

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-gray-600">Or upload file:</span>

        {!isUploaded && !uploading && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.doc,.docx,.txt"
              className="hidden"
              onChange={handlePick}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <UploadIcon className="w-4 h-4" />
              Choose file
            </button>
          </>
        )}

        {uploading && (
          <span className="inline-flex items-center gap-2 text-sm text-purple-600">
            <Loader className="w-4 h-4 animate-spin" />
            Uploading…
          </span>
        )}

        {isUploaded && !uploading && (
          <div className="flex items-center gap-2 px-3 py-1.5 text-sm bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
            <a
              href={value}
              target="_blank"
              rel="noreferrer"
              className="text-green-800 underline underline-offset-2 max-w-[220px] truncate"
              title={displayName}
            >
              {displayName}
            </a>
            <button
              type="button"
              onClick={() => onClear(name)}
              className="text-red-500 hover:text-red-700 ml-1 shrink-0"
              title="Remove file"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
const ReviewForm = () => {
  const { manuscriptId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [manuscriptData, setManuscriptData] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [totalSteps] = useState(6);
  const [draftStatus, setDraftStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const [formData, setFormData] = useState({
    paragraph_summary: '',
    general_comment: '',
    specific_comment: '',
    letter_to_editor: '',

    title_accuracy: null,
    abstract_summarize: null,

    man_present: null,
    accurate_summary: null,
    paper_purpose: null,

    clear_manuscript: null,
    clear_materials: null,
    best_practice: null,
    ethical_standards: null,

    study_find: null,
    result_present: null,
    complemet_result: null,
    specified_standard: null,
    distract_content: null,
    man_issues: null,

    key_findings: null,
    study_strenghts: null,
    compare_manu: null,
    discuss_manu: null,
    describe_manu: null,
    study_achievement: null,
    topic_gaps: null,
    topic_accuracy: null,

    novelty: null,
    quality: null,
    scientific_accuracy: null,
    overall_merit: null,
    english_level: null,

    recommendation: null,
  });

  const [files, setFiles] = useState({
    paragraph_summary_file: '',
    general_comment_file: '',
    specific_comment_file: '',
  });

  const [uploading, setUploading] = useState({
    paragraph_summary_file: false,
    general_comment_file: false,
    specific_comment_file: false,
  });

  const [scores, setScores] = useState({ table1: 0, table2: 0, total: 0 });
  const [errors, setErrors] = useState({});
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // -------------------------------------------------------------------------
  // Refs — used by the autosave machinery to always see the freshest state
  // and to serialize/queue saves so nothing overlaps.
  // -------------------------------------------------------------------------
  const inactivityTimerRef = useRef(null);
  const saveInFlightRef = useRef(false);       // true while a save request is active
  const pendingSaveRef = useRef(false);        // true if a save was requested mid-flight
  const formDataRef = useRef(formData);        // always-current form data
  const filesRef = useRef(files);              // always-current files
  const currentStepRef = useRef(currentStep);  // always-current step
  const isUnmountedRef = useRef(false);        // guard against setState after unmount

  // Keep refs in sync with state so async save callbacks never see stale values
  useEffect(() => { formDataRef.current = formData; }, [formData]);
  useEffect(() => { filesRef.current = files; }, [files]);
  useEffect(() => { currentStepRef.current = currentStep; }, [currentStep]);

  useEffect(() => {
    return () => { isUnmountedRef.current = true; };
  }, []);

  // -------------------------------------------------------------------------
  // Manuscript file helpers
  // -------------------------------------------------------------------------
  const fileFields = [
    { key: 'manuscript_file', label: 'Manuscript' },
    { key: 'document_file', label: 'Document' },
    { key: 'tracked_manuscript_file', label: 'Tracked Manuscript' },
    { key: 'cover_letter_file', label: 'Cover Letter' },
    { key: 'tables', label: 'Tables' },
    { key: 'figures', label: 'Figures' },
    { key: 'graphic_abstract', label: 'Graphic Abstract' },
    { key: 'supplementary_material', label: 'Supplementary Material' },
  ];

  const getManuscriptFiles = () => {
    if (!manuscriptData) return {};
    const out = {};
    fileFields.forEach(({ key, label }) => {
      if (manuscriptData[key]) out[label] = manuscriptData[key];
    });
    return out;
  };

  const handleDownloadAll = () => {
    const all = getManuscriptFiles();
    const entries = Object.entries(all);
    if (entries.length === 0) {
      showToast('No files available to download.', 'error');
      return;
    }
    entries.forEach(([label, fileName], i) =>
      setTimeout(() => handleDownloadFile(fileName, label), i * 500)
    );
    showToast(`Downloading ${entries.length} file(s)...`, 'info');
  };

  // -------------------------------------------------------------------------
  // Payload builder — reads from refs so it can be called from any context
  // (autosave timer, upload callback, nextStep) without stale closures.
  // -------------------------------------------------------------------------
  const buildPayload = useCallback(({ filesOverride, action = 'draft' } = {}) => {
    const mergedFiles = { ...filesRef.current, ...(filesOverride || {}) };
    const snapshot = formDataRef.current;

    const clean = {};
    Object.keys(snapshot).forEach((key) => {
      if (snapshot[key] !== null && snapshot[key] !== '') {
        clean[key] = snapshot[key];
      }
    });

    return {
      ...clean,
      manuscriptId,
      action,
      paragraph_summary_file: mergedFiles.paragraph_summary_file || '',
      general_comment_file: mergedFiles.general_comment_file || '',
      specific_comment_file: mergedFiles.specific_comment_file || '',
    };
  }, [manuscriptId]);

  // -------------------------------------------------------------------------
  // Core save routine — serialized. Never runs two saves concurrently.
  // Returns a Promise<boolean> so callers can await the outcome.
  // `silent` suppresses the toast (used for the background inactivity save).
  // -------------------------------------------------------------------------
  const performSave = useCallback((opts = {}) => {
    const { silent = true, filesOverride, action = 'draft' } = opts;

    // If a save is already running, queue one more and return the queued promise.
    if (saveInFlightRef.current) {
      pendingSaveRef.current = true;
      return Promise.resolve(false);
    }

    saveInFlightRef.current = true;
    if (!isUnmountedRef.current) setDraftStatus('saving');

    const payload = buildPayload({ filesOverride, action });

    return axios
      .post('/reviewer/submit-review', payload, {
        headers: { 'Content-Type': 'application/json' },
      })
      .then((res) => {
        if (!isUnmountedRef.current) {
          setDraftStatus('saved');
          setLastSavedAt(new Date());
        }
        if (!silent) {
          showToast(res.data?.message || 'Draft saved successfully!', 'success');
        }
        return true;
      })
      .catch((err) => {
        console.error('Auto-save failed:', err);
        if (!isUnmountedRef.current) setDraftStatus('error');
        if (!silent) {
          showToast(
            err.response?.data?.message || 'Failed to save draft. Please try again.',
            'error'
          );
        }
        return false;
      })
      .finally(() => {
        saveInFlightRef.current = false;

        // If a new save was requested while we were busy, run it now with
        // the freshest state from the refs.
        if (pendingSaveRef.current) {
          pendingSaveRef.current = false;
          // Fire-and-forget; the second save picks up latest values via refs.
          setTimeout(() => performSave({ silent: true, action: 'draft' }), 0);
        }
      });
  }, [buildPayload]);

  // Convenience wrappers used by UI event handlers
  const autoSaveDraft = useCallback((opts = {}) => performSave({ silent: true, ...opts }), [performSave]);
  const saveDraft = useCallback(() => performSave({ silent: false, action: 'draft' }), [performSave]);

  // -------------------------------------------------------------------------
  // Inactivity timer — resets on any user interaction. Fires silent save
  // after INACTIVITY_AUTOSAVE_MS of no interaction.
  // -------------------------------------------------------------------------
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    inactivityTimerRef.current = setTimeout(() => {
      // Only autosave if the form isn't already submitting.
      if (!submitting) {
        autoSaveDraft();
      }
    }, INACTIVITY_AUTOSAVE_MS);
  }, [autoSaveDraft, submitting]);

  useEffect(() => {
    // Start the timer on mount.
    resetInactivityTimer();

    // Reset on user activity. Using capture:true makes sure we catch
    // events even if something stops propagation.
    const handler = () => resetInactivityTimer();
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'input', 'change'];

    events.forEach((ev) =>
      document.addEventListener(ev, handler, { capture: true, passive: true })
    );

    return () => {
      events.forEach((ev) =>
        document.removeEventListener(ev, handler, { capture: true })
      );
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  }, [resetInactivityTimer]);

  // -------------------------------------------------------------------------
  // Data loading
  // -------------------------------------------------------------------------
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const manuscriptResponse = await axios.post('/editors/getSubmissionData', {
          id: manuscriptId,
        });
        if (manuscriptResponse.data.success) {
          setManuscriptData(manuscriptResponse.data.articles);
        }

        const draftResponse = await axios.get(`/reviewer/review-draft/${manuscriptId}`);

        if (draftResponse.data.hasDraft) {
          const scoreFields = [
            'title_accuracy', 'abstract_summarize',
            'man_present', 'accurate_summary', 'paper_purpose',
            'clear_manuscript', 'clear_materials', 'best_practice', 'ethical_standards',
            'study_find', 'result_present', 'complemet_result', 'specified_standard',
            'distract_content', 'man_issues', 'key_findings', 'study_strenghts',
            'compare_manu', 'discuss_manu', 'describe_manu', 'study_achievement',
            'topic_gaps', 'topic_accuracy', 'novelty', 'quality', 'scientific_accuracy',
            'overall_merit', 'english_level',
          ];

          const draftData = draftResponse.data.formData || {};
          const convertedData = {};

          Object.keys(draftData).forEach((key) => {
            if (scoreFields.includes(key)) {
              convertedData[key] =
                draftData[key] === null || draftData[key] === undefined || draftData[key] === ''
                  ? null
                  : Number(draftData[key]);
            } else {
              convertedData[key] = draftData[key];
            }
          });

          setFormData(prev => ({ ...prev, ...convertedData }));

          setScores({
            table1: Number(draftResponse.data.scores?.table1 || 0),
            table2: Number(draftResponse.data.scores?.table2 || 0),
            total: Number(draftResponse.data.scores?.total || 0),
          });

          if (draftResponse.data.files) {
            setFiles({
              paragraph_summary_file: draftResponse.data.files.paragraph_summary_file || '',
              general_comment_file: draftResponse.data.files.general_comment_file || '',
              specific_comment_file: draftResponse.data.files.specific_comment_file || '',
            });
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [manuscriptId]);

  useEffect(() => { calculateScores(); }, [formData]);

  useEffect(() => {
    const pct = ((currentStep - 1) / (totalSteps - 1)) * 100;
    document.documentElement.style.setProperty('--progress-width', `${pct}%`);
  }, [currentStep, totalSteps]);

  // -------------------------------------------------------------------------
  // Change handlers
  // -------------------------------------------------------------------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: parseInt(value) }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  // -------------------------------------------------------------------------
  // File upload + clear
  // -------------------------------------------------------------------------
  const handleFileUpload = async (fieldName, file) => {
    setUploading(prev => ({ ...prev, [fieldName]: true }));
    try {
      const fd = new FormData();
      fd.append('manuscriptId', manuscriptId);
      fd.append(fieldName, file);

      const res = await axios.post(
        `/reviewer/upload-review-file/${fieldName}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const url = res.data?.fileUrl || res.data?.url;
      if (!url) throw new Error(res.data?.message || 'Upload failed — no URL returned');

      setFiles(prev => ({ ...prev, [fieldName]: url }));
      // Update ref immediately so an autosave fired right now sees the new URL
      filesRef.current = { ...filesRef.current, [fieldName]: url };
      showToast(`${file.name} uploaded successfully`, 'success');

      // Persist the new URL right away — silent, since the toast already fired
      autoSaveDraft({ filesOverride: { [fieldName]: url } });
    } catch (err) {
      console.error('Upload error:', err);
      showToast(err.response?.data?.message || err.message || 'Upload failed', 'error');
    } finally {
      setUploading(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const clearFile = (fieldName) => {
    setFiles(prev => ({ ...prev, [fieldName]: '' }));
    filesRef.current = { ...filesRef.current, [fieldName]: '' };
    autoSaveDraft({ filesOverride: { [fieldName]: '' } });
  };

  // -------------------------------------------------------------------------
  // Scores
  // -------------------------------------------------------------------------
  const calculateScores = () => {
    const section1Fields = [
      'title_accuracy', 'abstract_summarize',
      'man_present', 'accurate_summary', 'paper_purpose',
      'clear_manuscript', 'clear_materials', 'best_practice', 'ethical_standards',
      'study_find', 'result_present', 'complemet_result', 'specified_standard', 'distract_content', 'man_issues',
      'key_findings', 'study_strenghts', 'compare_manu', 'discuss_manu', 'describe_manu',
      'study_achievement', 'topic_gaps', 'topic_accuracy',
    ];

    const section1Total = section1Fields.reduce((sum, f) => sum + (formData[f] || 0), 0);

    const section2Fields = ['novelty', 'quality', 'scientific_accuracy', 'overall_merit', 'english_level'];
    const section2Total = section2Fields.reduce((sum, f) => sum + (formData[f] || 0), 0);

    setScores({
      table1: section1Total,
      table2: section2Total,
      total: section1Total + section2Total,
    });
  };

  // -------------------------------------------------------------------------
  // Validation
  // -------------------------------------------------------------------------
  const validateStep = (step) => {
    const newErrors = {};

    if (step === 3) {
      const section1Fields = [
        'title_accuracy', 'abstract_summarize',
        'man_present', 'accurate_summary', 'paper_purpose',
        'clear_manuscript', 'clear_materials', 'best_practice', 'ethical_standards',
        'study_find', 'result_present', 'complemet_result', 'specified_standard', 'distract_content', 'man_issues',
        'key_findings', 'study_strenghts', 'compare_manu', 'discuss_manu', 'describe_manu',
        'study_achievement', 'topic_gaps', 'topic_accuracy',
      ];
      section1Fields.forEach((field) => {
        if (formData[field] === null) newErrors[field] = 'Please select a score or N/A';
      });
    }

    if (step === 4) {
      ['novelty', 'quality', 'scientific_accuracy', 'overall_merit', 'english_level'].forEach((field) => {
        if (formData[field] === null) newErrors[field] = 'Please select a score';
      });
    }

    if (step === 5) {
      if (!formData.recommendation) newErrors.recommendation = 'Please select a recommendation';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // -------------------------------------------------------------------------
  // Navigation — Next fires a save in the background and advances immediately.
  // -------------------------------------------------------------------------
  const nextStep = () => {
    if (!validateStep(currentStep)) return;
    if (currentStep >= totalSteps) return;

    // Fire-and-forget save. Does not block the user's navigation.
    autoSaveDraft();

    // Advance immediately. The save continues in the background and will
    // complete (or be retried via pendingSaveRef) regardless of the step.
    setCurrentStep(prev => prev + 1);
    window.scrollTo(0, 0);
  };

  const prevStep = () => {
    if (currentStep <= 1) return;
    autoSaveDraft();
    setCurrentStep(prev => prev - 1);
    window.scrollTo(0, 0);
  };

  // -------------------------------------------------------------------------
  // Final submit
  // -------------------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(5)) return;

    // Cancel any pending inactivity autosave — the final submit supersedes it.
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);

    setSubmitting(true);
    const overlay = document.querySelector('.overlayX');
    if (overlay) overlay.style.display = 'flex';

    try {
      // Wait for any in-flight save to finish before submitting so we don't
      // race a stale draft overwrite against the final submit.
      const waitForInFlight = () =>
        new Promise((resolve) => {
          const check = () => {
            if (!saveInFlightRef.current) resolve();
            else setTimeout(check, 100);
          };
          check();
        });
      await waitForInFlight();

      const res = await axios.post(
        '/reviewer/submit-review',
        buildPayload({ action: 'submit' }),
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (overlay) overlay.style.display = 'none';

      if (res.data.status === 'success') {
        showToast('Review submitted successfully!', 'success');
        if (res.data.editorInvited) {
          showToast('The responsible editor has been notified to make a decision.', 'success');
        }
        navigate('/reviewerdash/submitted');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      if (overlay) overlay.style.display = 'none';
      showToast(
        error.response?.data?.message || 'Failed to submit review. Please try again.',
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Score row renderer
  // -------------------------------------------------------------------------
  const renderScoreRadioGroup = (name, label, showNA = true) => {
    const scoreOptions = showNA ? [1, 2, 3, 4, 5, 0] : [1, 2, 3, 4, 5];
    const naIndex = showNA ? 5 : -1;

    return (
      <>
        <tr className={errors[name] ? 'bg-red-50' : ''}>
          <td className="px-4 py-3 text-sm text-gray-600 border border-gray-300">{label}</td>
          {scoreOptions.map((score, index) => (
            <td key={index} className="px-2 py-3 text-center border border-gray-300">
              <input
                type="radio"
                name={name}
                value={score}
                checked={formData[name] === score}
                onChange={handleRadioChange}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500"
              />
              {showNA && index === naIndex && <span className="ml-1 text-xs text-gray-500">N/A</span>}
            </td>
          ))}
        </tr>
        {errors[name] && (
          <tr>
            <td colSpan={showNA ? 7 : 6} className="text-red-500 text-xs p-1">
              {errors[name]}
            </td>
          </tr>
        )}
      </>
    );
  };

  // -------------------------------------------------------------------------
  // Small status pill for the draft state
  // -------------------------------------------------------------------------
  const renderDraftStatus = () => {
    if (draftStatus === 'saving') {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-purple-600">
          <Loader className="w-3.5 h-3.5 animate-spin" />
          Saving…
        </span>
      );
    }
    if (draftStatus === 'saved' && lastSavedAt) {
      const seconds = Math.floor((Date.now() - lastSavedAt.getTime()) / 1000);
      const label =
        seconds < 60
          ? 'Saved just now'
          : `Saved ${Math.floor(seconds / 60)} min ago`;
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-green-600">
          <CheckCircle className="w-3.5 h-3.5" />
          {label}
        </span>
      );
    }
    if (draftStatus === 'error') {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-red-500">
          <AlertCircle className="w-3.5 h-3.5" />
          Save failed — retrying…
        </span>
      );
    }
    return null;
  };

  // -------------------------------------------------------------------------
  // Loading state
  // -------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading manuscript data...</p>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="overlayX hidden fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 text-center">
          <Loader className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <div className="font-bold text-black">Submitting Review...please wait...</div>
        </div>
      </div>

      {/* Notice Section */}
      <div className="bg-white rounded-lg shadow-md mb-6 overflow-hidden">
        <div className="bg-red-50 border-l-4 border-red-500 px-6 py-4 flex justify-between items-center flex-wrap gap-2">
          <h5 className="text-red-800 font-bold text-lg flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
            THE REVIEWER'S REPORT
          </h5>
          <div className="flex items-center gap-4">
            {renderDraftStatus()}
            <span className="text-sm text-gray-600">Manuscript ID: {manuscriptId}</span>
          </div>
        </div>
        <div className="p-6">
          <p className="text-gray-600 text-sm">
            Please outline your review report in the following structure.
          </p>
        </div>
      </div>

      {/* Manuscript Info Bar */}
      {manuscriptData && (
        <div className="bg-white rounded-lg shadow-md p-4 mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center space-x-4">
            <FileText className="w-5 h-5 text-purple-600" />
            <div>
              <h3 className="font-semibold text-gray-900">{manuscriptData.title}</h3>
              <p className="text-sm text-gray-500">
                {manuscriptData.authors?.length > 0 && (
                  <span>by {manuscriptData.authors?.map(a => a.name || a).join(', ')}</span>
                )}
                {manuscriptData.corresponding_author_fullname && (
                  <span>
                    {manuscriptData.authors?.length > 0 && ' · '}
                    Corresponding Author: {manuscriptData.corresponding_author_fullname}
                  </span>
                )}
                {!manuscriptData.authors?.length && !manuscriptData.corresponding_author_fullname && (
                  <span>No author information available</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowPreviewModal(true)}
              className="px-3 py-1 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              Preview
            </button>
            <button
              onClick={handleDownloadAll}
              className="px-3 py-1 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download All
            </button>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="progress-bar">
          {[1, 2, 3, 4, 5, 6].map((step) => (
            <div
              key={step}
              className={`progress-step ${
                step < currentStep
                  ? 'progress-step-completed'
                  : step === currentStep
                  ? 'progress-step-active'
                  : ''
              }`}
              data-step={step}
            >
              {step < currentStep ? <CheckCircle className="w-4 h-4" /> : step}
              <span className="step-label">
                {step === 1 && 'Summary'}
                {step === 2 && 'Comments'}
                {step === 3 && 'Scoring 1'}
                {step === 4 && 'Scoring 2'}
                {step === 5 && 'Recommendation'}
                {step === 6 && 'Confidential'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 mb-6">
        <input type="hidden" name="article_id" value={manuscriptId} />

        {/* Step 1 */}
        <div className={`form-step ${currentStep === 1 ? 'form-step-active' : ''}`} id="step-1">
          <h3 className="text-lg font-semibold text-purple-600 mb-4">Step 1: Summary Comments</h3>
          <div className="mb-8">
            <h4 className="font-medium text-gray-700 mb-2">One paragraph summary:</h4>
            <p className="text-sm text-gray-500 mb-4">
              In one paragraph, please summarize the content of the manuscript, highlighting the key findings and strengths of the study.
            </p>
            <textarea
              name="paragraph_summary"
              value={formData.paragraph_summary}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              rows="5"
              placeholder="Enter your summary here..."
            />
            <ReviewFileUpload
              name="paragraph_summary_file"
              value={files.paragraph_summary_file}
              uploading={uploading.paragraph_summary_file}
              onUpload={handleFileUpload}
              onClear={clearFile}
            />
          </div>
        </div>

        {/* Step 2 */}
        <div className={`form-step ${currentStep === 2 ? 'form-step-active' : ''}`} id="step-2">
          <h3 className="text-lg font-semibold text-purple-600 mb-4">Step 2: Detailed Comments</h3>

          <div className="mb-8">
            <h4 className="font-medium text-gray-700 mb-2">General Comments:</h4>
            <p className="text-sm text-gray-500 mb-4">
              Please provide general comments and criticisms that highlight the scientific content of the manuscript.
            </p>
            <textarea
              name="general_comment"
              value={formData.general_comment}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              rows="5"
              placeholder="Enter your general comments here..."
            />
            <ReviewFileUpload
              name="general_comment_file"
              value={files.general_comment_file}
              uploading={uploading.general_comment_file}
              onUpload={handleFileUpload}
              onClear={clearFile}
            />
          </div>

          <div className="mb-8">
            <h4 className="font-medium text-gray-700 mb-2">Specific comments:</h4>
            <p className="text-sm text-gray-500 mb-4">
              Please provide specific comments to the authors throughout the manuscript.
            </p>
            <textarea
              name="specific_comment"
              value={formData.specific_comment}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              rows="5"
              placeholder="Enter your specific comments here..."
            />
            <ReviewFileUpload
              name="specific_comment_file"
              value={files.specific_comment_file}
              uploading={uploading.specific_comment_file}
              onUpload={handleFileUpload}
              onClear={clearFile}
            />
          </div>
        </div>

        {/* Step 3 */}
        <div className={`form-step ${currentStep === 3 ? 'form-step-active' : ''}`} id="step-3">
          <h3 className="text-lg font-semibold text-purple-600 mb-4">Step 3: Manuscript Scoring (Part 1)</h3>
          <div className="mb-8">
            <h4 className="font-medium text-gray-700 mb-2">SCORING SPECIFIC SECTION OF THE MANUSCRIPT</h4>
            <p className="text-sm text-gray-500 mb-4">
              Please score this section as objectively as possible (1=Poor, 5=Excellent, N/A=0)
            </p>

            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-300">
                <thead>
                  <tr className="bg-purple-50">
                    <th className="p-3 border border-gray-300 text-left">QUESTIONS</th>
                    <th colSpan="6" className="p-3 border border-gray-300 text-center">SCORES</th>
                  </tr>
                  <tr className="bg-purple-100">
                    <th className="p-2 border border-gray-300"></th>
                    <th className="p-2 border border-gray-300 text-center">1</th>
                    <th className="p-2 border border-gray-300 text-center">2</th>
                    <th className="p-2 border border-gray-300 text-center">3</th>
                    <th className="p-2 border border-gray-300 text-center">4</th>
                    <th className="p-2 border border-gray-300 text-center">5</th>
                    <th className="p-2 border border-gray-300 text-center">N/A</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-purple-50">
                    <td colSpan="7" className="p-3 font-bold text-purple-800">QUESTIONS REGARDING TITLE AND ABSTRACT</td>
                  </tr>
                  {renderScoreRadioGroup('title_accuracy', 'Does the title accurately reflect the subject of the manuscript?')}
                  {renderScoreRadioGroup('abstract_summarize', 'Does the abstract clearly summarize the content of the manuscript?')}

                  <tr className="bg-purple-50">
                    <td colSpan="7" className="p-3 font-bold text-purple-800">QUESTIONS REGARDING INTRODUCTION</td>
                  </tr>
                  {renderScoreRadioGroup('man_present', 'Does the manuscript present what is already known and unknown (gaps) on the topic in the introduction section?')}
                  {renderScoreRadioGroup('accurate_summary', 'Does the manuscript give an accurate summary of key recent research on the topic in the introduction section?')}
                  {renderScoreRadioGroup('paper_purpose', 'Is the purpose (aim or objective) of the paper, its originality and novelty clear as indicated in the introduction section?')}

                  <tr className="bg-purple-50">
                    <td colSpan="7" className="p-3 font-bold text-purple-800">QUESTIONS REGARDING STUDY METHODS AND ETHICS</td>
                  </tr>
                  {renderScoreRadioGroup('clear_manuscript', 'Is the methods section of the manuscript clear and what was done clearly and accurately described?')}
                  {renderScoreRadioGroup('clear_materials', 'Are the study materials, methods, instruments used, and measurements made clearly described?')}
                  {renderScoreRadioGroup('best_practice', 'Are the research methods valid, reliable, reproducible, and meet requirements for best practice?')}
                  {renderScoreRadioGroup('ethical_standards', 'Are ethical standards followed in implementing the research and in writing the manuscript?')}

                  <tr className="bg-purple-50">
                    <td colSpan="7" className="p-3 font-bold text-purple-800">QUESTIONS REGARDING RESULTS, GRAPHICS, AND TABLES</td>
                  </tr>
                  {renderScoreRadioGroup('study_find', 'What did the study find and has this been clearly described?')}
                  {renderScoreRadioGroup('result_present', 'Are the results of the manuscript presented in a logical and coherent manner?')}
                  {renderScoreRadioGroup('complemet_result', 'Do the graphics used (tables and figures) clearly complement the results?')}
                  {renderScoreRadioGroup('specified_standard', 'Have the tables, graphics, figures, images followed highest specified standards?')}
                  {renderScoreRadioGroup('distract_content', 'Do the tables, graphics, figures, images add value or distract from the content of the manuscript?')}
                  {renderScoreRadioGroup('man_issues', 'Are there issues with titles, labels, statistical notation or image quality of tables, graphics, figures, images included in the manuscript?')}

                  <tr className="bg-purple-50">
                    <td colSpan="7" className="p-3 font-bold text-purple-800">QUESTIONS REGARDING DISCUSSION</td>
                  </tr>
                  {renderScoreRadioGroup('key_findings', 'Does the manuscript present the summary of the key findings?')}
                  {renderScoreRadioGroup('study_strenghts', 'Does the manuscript highlight the strengths and limitations of the study?')}
                  {renderScoreRadioGroup('compare_manu', 'Does the manuscript compare its findings to similar papers on the topic?')}
                  {renderScoreRadioGroup('discuss_manu', 'Does the manuscript discuss the meaning and implications of the findings?')}
                  {renderScoreRadioGroup('describe_manu', 'Does the manuscript describe and discuss the overall story formed so far on the topic?')}
                  {renderScoreRadioGroup('study_achievement', 'Do the conclusions reflect the achievement of the study aims?')}
                  {renderScoreRadioGroup('topic_gaps', 'Does the manuscript discuss the gaps or inconsistencies on the topic and ways forward described?')}
                  {renderScoreRadioGroup('topic_accuracy', 'Is the referencing accurate, adequate and balance in relation to the topic of the manuscript?')}
                </tbody>
                <tfoot className="bg-purple-100">
                  <tr>
                    <td className="px-4 py-3 text-sm font-bold text-gray-700 border border-gray-300">Section 1 Total</td>
                    <td colSpan="6" className="px-4 py-3 text-center font-bold text-purple-700 border border-gray-300">
                      {scores.table1}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        {/* Step 4 */}
        <div className={`form-step ${currentStep === 4 ? 'form-step-active' : ''}`} id="step-4">
          <h3 className="text-lg font-semibold text-purple-600 mb-4">Step 4: Manuscript Scoring (Part 2)</h3>
          <div className="mb-8">
            <h4 className="font-medium text-gray-700 mb-2">OVERALL RATING OF THE MANUSCRIPT</h4>
            <p className="text-sm text-gray-500 mb-4">Please score this section as objectively as possible (1=Poor, 5=Excellent)</p>

            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-300">
                <thead>
                  <tr className="bg-purple-50">
                    <th className="p-3 border border-gray-300 text-left">CRITERIA / QUESTION</th>
                    <th colSpan="5" className="p-3 border border-gray-300 text-center">SCORES</th>
                  </tr>
                  <tr className="bg-purple-100">
                    <th className="p-2 border border-gray-300"></th>
                    <th className="p-2 border border-gray-300 text-center">1</th>
                    <th className="p-2 border border-gray-300 text-center">2</th>
                    <th className="p-2 border border-gray-300 text-center">3</th>
                    <th className="p-2 border border-gray-300 text-center">4</th>
                    <th className="p-2 border border-gray-300 text-center">5</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className={errors.novelty ? 'bg-red-50' : ''}>
                    <td className="px-4 py-3 text-sm text-gray-600 border border-gray-300">
                      <span className="font-medium">NOVELTY:</span> Does the manuscript address an original and well-defined question? Do the findings of the manuscript advance current knowledge on the topic area?
                    </td>
                    {[1, 2, 3, 4, 5].map(score => (
                      <td key={score} className="px-2 py-3 text-center border border-gray-300">
                        <input
                          type="radio"
                          name="novelty"
                          value={score}
                          checked={formData.novelty === score}
                          onChange={handleRadioChange}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                        />
                      </td>
                    ))}
                  </tr>
                  {errors.novelty && <tr><td colSpan="6" className="text-red-500 text-xs p-1">{errors.novelty}</td></tr>}

                  <tr className={errors.quality ? 'bg-red-50' : ''}>
                    <td className="px-4 py-3 text-sm text-gray-600 border border-gray-300">
                      <span className="font-medium">QUALITY:</span> Does the manuscript adhere to highest standard of writing and presentation of its findings? Are the manuscript sections appropriately written?
                    </td>
                    {[1, 2, 3, 4, 5].map(score => (
                      <td key={score} className="px-2 py-3 text-center border border-gray-300">
                        <input
                          type="radio"
                          name="quality"
                          value={score}
                          checked={formData.quality === score}
                          onChange={handleRadioChange}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                        />
                      </td>
                    ))}
                  </tr>
                  {errors.quality && <tr><td colSpan="6" className="text-red-500 text-xs p-1">{errors.quality}</td></tr>}

                  <tr className={errors.scientific_accuracy ? 'bg-red-50' : ''}>
                    <td className="px-4 py-3 text-sm text-gray-600 border border-gray-300">
                      <span className="font-medium">SCIENTIFIC ACCURACY:</span> Was the study design correct and sound? Do the methods employed follow expected standards in the study context? Are the data analyses choices and implementation of the highest technical standards within the scope of the topic area? Are the data and emanated results sufficiently robust to draw conclusions?
                    </td>
                    {[1, 2, 3, 4, 5].map(score => (
                      <td key={score} className="px-2 py-3 text-center border border-gray-300">
                        <input
                          type="radio"
                          name="scientific_accuracy"
                          value={score}
                          checked={formData.scientific_accuracy === score}
                          onChange={handleRadioChange}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                        />
                      </td>
                    ))}
                  </tr>
                  {errors.scientific_accuracy && <tr><td colSpan="6" className="text-red-500 text-xs p-1">{errors.scientific_accuracy}</td></tr>}

                  <tr className={errors.overall_merit ? 'bg-red-50' : ''}>
                    <td className="px-4 py-3 text-sm text-gray-600 border border-gray-300">
                      <span className="font-medium">OVERALL MERIT:</span> Does the manuscript have an overall benefit to warrant publication in ASFIRJ?
                    </td>
                    {[1, 2, 3, 4, 5].map(score => (
                      <td key={score} className="px-2 py-3 text-center border border-gray-300">
                        <input
                          type="radio"
                          name="overall_merit"
                          value={score}
                          checked={formData.overall_merit === score}
                          onChange={handleRadioChange}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                        />
                      </td>
                    ))}
                  </tr>
                  {errors.overall_merit && <tr><td colSpan="6" className="text-red-500 text-xs p-1">{errors.overall_merit}</td></tr>}

                  <tr className={errors.english_level ? 'bg-red-50' : ''}>
                    <td className="px-4 py-3 text-sm text-gray-600 border border-gray-300">
                      <span className="font-medium">ENGLISH LEVEL:</span> Is the English language appropriate and understandable?
                    </td>
                    {[1, 2, 3, 4, 5].map(score => (
                      <td key={score} className="px-2 py-3 text-center border border-gray-300">
                        <input
                          type="radio"
                          name="english_level"
                          value={score}
                          checked={formData.english_level === score}
                          onChange={handleRadioChange}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                        />
                      </td>
                    ))}
                  </tr>
                  {errors.english_level && <tr><td colSpan="6" className="text-red-500 text-xs p-1">{errors.english_level}</td></tr>}
                </tbody>
                <tfoot className="bg-purple-100">
                  <tr>
                    <td className="px-4 py-3 text-sm font-bold text-gray-700 border border-gray-300">Section 2 Total</td>
                    <td colSpan="5" className="px-4 py-3 text-center font-bold text-purple-700 border border-gray-300">
                      {scores.table2}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        {/* Step 5 */}
        <div className={`form-step ${currentStep === 5 ? 'form-step-active' : ''}`} id="step-5">
          <h3 className="text-lg font-semibold text-purple-600 mb-4">Step 5: Recommendation</h3>
          <div className="mb-8">
            <h4 className="font-medium text-gray-700 mb-2">OVERALL RECOMMENDATION</h4>
            <p className="text-sm text-gray-500 mb-4">
              Your overall recommendation should come in either of the four underlisted decision paths.
            </p>

            {errors.recommendation && (
              <div className="mb-3 text-red-500 text-sm">{errors.recommendation}</div>
            )}

            <div className="space-y-3">
              {[
                { value: 'Accept As It Is', desc: 'The manuscript can be accepted without any further changes.' },
                { value: 'Accept Following Minor Revisions', desc: 'The paper can be accepted after satisfactory minor revisions on the basis of the comments raised.' },
                { value: 'Reconsider Following Major Revisions', desc: 'The manuscript can be accepted after satisfactory major revisions on the basis of the comments raised.' },
                { value: 'Reject', desc: 'The manuscript contains serious flaws and does not offer any original contribution.' },
              ].map(({ value, desc }) => (
                <label
                  key={value}
                  className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                    formData.recommendation === value
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="recommendation"
                    value={value}
                    checked={formData.recommendation === value}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 mr-3"
                  />
                  <div>
                    <span className="font-medium text-gray-900">{value}</span>
                    <p className="text-sm text-gray-600 mt-1">{desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Step 6 */}
        <div className={`form-step ${currentStep === 6 ? 'form-step-active' : ''}`} id="step-6">
          <h3 className="text-lg font-semibold text-purple-600 mb-4">Step 6: Confidential Comments</h3>

          <div className="mb-8">
            <h4 className="font-medium text-gray-700 mb-2">Confidential Comment to the Editor (Optional):</h4>
            <textarea
              name="letter_to_editor"
              value={formData.letter_to_editor}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              rows="5"
              placeholder="Enter any confidential comments for the editor here..."
            />
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
            <p className="text-sm text-yellow-800 flex items-start">
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>
                <span className="font-medium">Note:</span> These comments will only be visible to the editor and will not be shared with the authors.
              </span>
            </p>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-8">
            <h4 className="font-semibold text-gray-900 mb-4">Review Summary</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Section 1 Score:</p>
                <p className="text-2xl font-bold text-purple-600">{scores.table1}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Section 2 Score:</p>
                <p className="text-2xl font-bold text-purple-600">{scores.table2}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Score:</p>
                <p className="text-2xl font-bold text-purple-600">{scores.total}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Recommendation:</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formData.recommendation || 'Not selected'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-8 pt-4 border-t border-gray-200">
          {currentStep > 1 ? (
            <button
              type="button"
              onClick={prevStep}
              disabled={submitting}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
          ) : (
            <div />
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={saveDraft}
              disabled={submitting || draftStatus === 'saving'}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
            >
              {draftStatus === 'saving' ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Draft
                </>
              )}
            </button>

            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={nextStep}
                disabled={submitting}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 inline-flex items-center gap-2 disabled:opacity-50"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
            )}
          </div>
        </div>
      </form>

      {/* Preview Modal */}
      {showPreviewModal && manuscriptData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Manuscript Details</h3>
                    <p className="text-sm text-gray-500">Preview manuscript information</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <p className="text-sm font-medium text-purple-600 mb-1">{manuscriptId}</p>
                  <h2 className="text-xl font-bold text-gray-900">{manuscriptData.title}</h2>
                  <p className="text-sm text-gray-500 mt-1">{manuscriptData.type}</p>
                </div>

                {manuscriptData.authors?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Authors</h4>
                    <div className="flex flex-wrap gap-2">
                      {manuscriptData.authors.map((author, index) => (
                        <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                          {author.name || author}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {manuscriptData.corresponding_author_fullname && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Corresponding Author</h4>
                    <p className="text-sm text-gray-900">{manuscriptData.corresponding_author_fullname}</p>
                    <p className="text-sm text-gray-600 flex items-center mt-1">
                      <Mail className="w-4 h-4 text-gray-400 mr-2" />
                      {manuscriptData.corresponding_authors_email}
                    </p>
                  </div>
                )}

                {manuscriptData.abstract && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Abstract</h4>
                    <div className="bg-gray-50 p-4 rounded-lg prose max-w-none">
                      <RichTextViewer content={manuscriptData.abstract} />
                    </div>
                  </div>
                )}

                {manuscriptData.keywords && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Keywords</h4>
                    <div className="flex flex-wrap gap-2">
                      {manuscriptData.keywords.split(',').map((keyword, i) => (
                        <span key={i} className="px-3 py-1 bg-purple-50 text-purple-700 text-sm rounded-full">
                          {keyword.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {manuscriptData.created_at && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Submitted Date</h4>
                      <p className="text-sm text-gray-600 flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                        {new Date(manuscriptData.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  {manuscriptData.status && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Status</h4>
                      <p className="text-sm text-gray-600 flex items-center">
                        <Clock className="w-4 h-4 text-gray-400 mr-2" />
                        {manuscriptData.status}
                      </p>
                    </div>
                  )}
                </div>

                {Object.keys(getManuscriptFiles()).length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <Paperclip className="w-4 h-4 mr-2 text-gray-400" />
                      Files
                    </h4>
                    <div className="space-y-2">
                      {Object.entries(getManuscriptFiles()).map(([label, fileName]) => (
                        <div key={label} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <FileText className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-700">{label}:</span>
                            <span className="ml-2 text-sm text-gray-600">{fileName}</span>
                          </div>
                          <button
                            className="p-1 text-gray-500 hover:text-purple-600 hover:bg-white rounded transition-colors"
                            onClick={() => handleDownloadFile(fileName, label)}
                            title={`Download ${fileName}`}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-8 flex justify-end">
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewForm;